import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "../css/BlastComponent.css";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import { phylotree } from "phylotree";
import { Tabs, TabList, Tab, TabPanel } from "react-tabs";
import "react-tabs/style/react-tabs.css";

// Utility functions
const getAlignmentStats = (query, subject) => {
  let matches = 0, mismatches = 0, gaps = 0;
  for (let i = 0; i < query.length; i++) {
    const q = query[i], s = subject[i];
    if (q === "-" || s === "-") gaps++;
    else if (q === s) matches++;
    else mismatches++;
  }
  return { matches, mismatches, gaps, length: query.length };
};

const getAlignmentSymbols = (query, subject) => {
  let symbols = "";
  for (let i = 0; i < query.length; i++) {
    const q = query[i], s = subject[i];
    if (q === "-" || s === "-") symbols += "-";
    else if (q === s) symbols += "|";
    else symbols += ".";
  }
  return symbols;
};

const renderColoredBlock = (seq, symbols) =>
  seq.split("").map((c, i) => {
    const s = symbols[i];
    let cls = "";
    if (s === "|") cls = "match";
    else if (s === ".") cls = "mismatch";
    else if (s === "-") cls = "gap";
    return <span key={i} className={cls}>{c}</span>;
  });

function AlignmentLens({ hovered }) {
  if (!hovered) return null;
  return (
    <div
      style={{
        position: "absolute",
        background: "#fff",
        border: "1px solid #ccc",
        padding: "5px 10px",
        borderRadius: "4px",
        boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
        pointerEvents: "none",
      }}
    >
      <strong>{hovered.id}</strong> — {hovered.identity}%
    </div>
  );
}

// --- Generate aligned lines with position labels ---
const generateAlignedLines = (
  querySeq,
  subjectSeq,
  startQuery = 1,
  startSubject = 1,
  blockSize = 60
) => {
  const lines = [];
  let qPos = startQuery;
  let sPos = startSubject;
  for (let i = 0; i < querySeq.length; i += blockSize) {
    const qBlock = querySeq.slice(i, i + blockSize);
    const sBlock = subjectSeq.slice(i, i + blockSize);
    const symbols = getAlignmentSymbols(qBlock, sBlock);
    lines.push({
      qStart: qPos,
      qEnd: qPos + qBlock.length - 1,
      sStart: sPos,
      sEnd: sPos + sBlock.length - 1,
      qSeq: qBlock,
      sSeq: sBlock,
      symbols: symbols,
    });
    qPos += qBlock.length;
    sPos += sBlock.length;
  }
  return lines;
};

// =================== Color function (based on bit score) ===================
const getBlastColorByScore = (bit_score) => {
  if (bit_score > 200) return "#2ecc71";
  if (bit_score > 150) return "#e74c3c";
  if (bit_score > 100) return "#e67e22";
  if (bit_score > 50) return "#3498db";
  return "#f1c40f";
};

const BlastLegend = () => (
  <div style={{ display: "flex", gap: 16, marginBottom: 12 }}>
    <LegendItem color="#2ecc71" label=">200" />
    <LegendItem color="#e74c3c" label="200–151" />
    <LegendItem color="#e67e22" label="150–101" />
    <LegendItem color="#3498db" label="100–51" />
    <LegendItem color="#f1c40f" label="≤50" />
  </div>
);

const LegendItem = ({ color, label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
    <div style={{
      width: 18,
      height: 8,
      background: color,
      borderRadius: 4
    }} />
    <span style={{ fontSize: 12 }}>{label}</span>
  </div>
);

// --- Main component ---
const BlastComponent = () => {
  const [sequenceInput, setSequenceInput] = useState("");
  const [blastType, setBlastType] = useState("blastn");
  const [genome, setGenome] = useState("default_genome");
  const [proteinDB, setProteinDB] = useState("default_protein");
  const [expectValue, setExpectValue] = useState("1e-5");
  const [maxAlignments, setMaxAlignments] = useState(50);
  const [wordSize, setWordSize] = useState(11);
  const [alignmentMode, setAlignmentMode] = useState("auto");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [blastSource, setBlastSource] = useState("local");

  const [hoveredAlignment, setHoveredAlignment] = useState(null);
  const [results, setResults] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progressMsg, setProgressMsg] = useState("");
  const [noResultsInfo, setNoResultsInfo] = useState(null);

  const [selectedAlignmentIndex, setSelectedAlignmentIndex] = useState(null);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [showAllAlignments, setShowAllAlignments] = useState(false);
  const [displayAlignments, setDisplayAlignments] = useState([]);
  const [showGraphics, setShowGraphics] = useState(false);
  const [hoveredResult, setHoveredResult] = useState(null);
  const [showAllHitsGraphically, setShowAllHitsGraphically] = useState(false);
  const [selectedHitIndex, setSelectedHitIndex] = useState(null);
  const [hoveredHit, setHoveredHit] = useState(null);
  const [selectedHit, setSelectedHit] = useState(null);
  const [expandedHitIndex, setExpandedHitIndex] = useState(null);
  const [showFullGraphics, setShowFullGraphics] = useState(false);
  const [showHelp, setShowHelp] = React.useState(false);
  const [showWhyNoResults, setShowWhyNoResults] = useState(false);

  const treeRef = useRef(null);
  const navigate = useNavigate();

  // File upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setSequenceInput(ev.target.result.trim());
    reader.readAsText(file);
  };
  const clearSequence = () => setSequenceInput("");

  const handleBack = () => {
    setResults([]);
    setMetadata(null);
    setError(null);
    setNoResultsInfo(null);
    setSelectedAlignmentIndex(null);
    setSelectedTabIndex(0);
    setShowAllAlignments(false);
    setDisplayAlignments([]);
    setShowGraphics(false);
    setHoveredResult(null);
    setShowAllHitsGraphically(false);
    setShowWhyNoResults(false);
  };

  const handleHitClick = (hit) => {
    setSelectedHit(hit);
  };

  const renderHitDetails = () => {
    if (!selectedHit) return null;
    return (
      <div style={{ marginTop: '20px', padding: '10px', border: '1px solid #ccc' }}>
        <button onClick={() => setSelectedHit(null)} style={{ float: 'right' }}>Close</button>
        <h4>Hit Details</h4>
        <p><strong>Title:</strong> {selectedHit.title}</p>
        <p><strong>%Identity:</strong> {selectedHit.percent_identity.toFixed(2)}%</p>
        <p><strong>E-value:</strong> {selectedHit.evalue}</p>
        <p><strong>Score:</strong> {selectedHit.bit_score}</p>
        <p><strong>Match:</strong> {selectedHit.match}</p>
        <p><strong>Mismatch:</strong> {selectedHit.mismatch}</p>
        <p><strong>Gaps:</strong> {selectedHit.gaps}</p>
      </div>
    );
  };

  const extractAccession = (title) => {
    if (!title) return "";
    const pipeMatch = title.match(/\b(?:sp|tr|ref|gb|emb|dbj)\|([^|]+)\|/i);
    if (pipeMatch) return pipeMatch[1];
    const accMatch = title.match(/\b[A-Z]{1,3}_?\d+\.\d+\b/);
    if (accMatch) return accMatch[0];
    return title.split(/\s+/)[0];
  };

  // --- Analyze sequence for "Why No Results" ---
  const analyzeSequence = (sequence, blastType) => {
    const seqUpper = sequence.toUpperCase();
    const analysis = [];
    
    if (blastType === 'blastx') {
      if (!seqUpper.includes('ATG')) {
        analysis.push('⚠️ No start codon (ATG) found - this sequence may not be protein-coding');
      } else if (!seqUpper.substring(0, 50).includes('ATG')) {
        analysis.push('⚠️ Start codon (ATG) not found near the beginning - may not be a complete CDS');
      }
      
      if (!seqUpper.includes('TAA') && !seqUpper.includes('TAG') && !seqUpper.includes('TGA')) {
        analysis.push('⚠️ No stop codon found (TAA, TAG, TGA) - sequence may be incomplete');
      }
      
      if (seqUpper.includes('GT') && seqUpper.includes('AG')) {
        analysis.push('ℹ️ Contains GT-AG splice sites - may be an intron (non-coding)');
      }
      
      if (seqUpper.includes('CGACC') && seqUpper.includes('TGCCG')) {
        analysis.push('ℹ️ Contains rRNA-like motifs - this is non-coding RNA');
      }
      
      if (seqUpper.length < 150) {
        analysis.push(`ℹ️ Short sequence (${seqUpper.length}bp) - may not contain enough information for significant matches`);
      }
    }
    
    const nCount = (seqUpper.match(/N/g) || []).length;
    if (nCount > 0) {
      analysis.push(`ℹ️ Contains ${nCount} ambiguous base(s) (N) - these can reduce match sensitivity`);
    }
    
    const gcCount = (seqUpper.match(/[GC]/g) || []).length;
    if (seqUpper.length > 0) {
      const gcRatio = gcCount / seqUpper.length;
      if (gcRatio < 0.25) {
        analysis.push(`ℹ️ Low GC content (${(gcRatio * 100).toFixed(1)}%) - may affect BLAST sensitivity`);
      } else if (gcRatio > 0.75) {
        analysis.push(`ℹ️ High GC content (${(gcRatio * 100).toFixed(1)}%) - may affect BLAST sensitivity`);
      }
    }
    
    return analysis;
  };

  // --- NCBI BLAST fetch ---
  const submitNCBIBlast = async (sequence, blastProgram = "blastn") => {
    const url = "https://blast.ncbi.nlm.nih.gov/Blast.cgi";
    const params = new URLSearchParams();
    params.append("CMD", "Put");
    params.append("PROGRAM", blastProgram);
    params.append("DATABASE", blastProgram === "blastp" ? "nr" : "nt");
    params.append("QUERY", sequence);

    const res = await fetch(`${url}?${params.toString()}`);
    if (!res.ok) throw new Error(`Network error: ${res.status}`);
    const text = await res.text();
    const ridMatch = text.match(/RID = (\S+)/);
    if (!ridMatch) throw new Error("Failed to submit BLAST job to NCBI");
    return { rid: ridMatch[1] };
  };

  // --- NCBI BLAST fetch results ---
  const fetchNCBIReturnResults = async (rid) => {
    const url = "https://blast.ncbi.nlm.nih.gov/Blast.cgi";
    while (true) {
      await new Promise(r => setTimeout(r, 5000));
      const text = await fetch(`${url}?CMD=Get&RID=${rid}`).then(r => r.text());
      if (text.includes("Status=READY")) break;
      if (text.includes("Status=FAILED")) throw new Error("NCBI BLAST failed");
    }
    return fetch(`${url}?CMD=Get&RID=${rid}&FORMAT_TYPE=JSON2`).then(r => r.json());
  };

  // --- Run handler ---
  const handleRun = async () => {
    if (!sequenceInput.trim()) {
      setError("You must enter a sequence or upload a file.");
      return;
    }

    if (blastSource === "ncbi") {
      try {
        setLoading(true);
        setError(null);
        setProgressMsg("Submitting to NCBI BLAST...");

        const { rid } = await submitNCBIBlast(sequenceInput.trim(), blastType);
        setProgressMsg("Waiting for NCBI results...");

        const ncbiResults = await fetchNCBIReturnResults(rid);
        const hits = ncbiResults?.BlastOutput2?.report?.results?.search?.hits || [];

        const mappedResults = hits.map(hit => {
          const hsp = hit.hsps?.[0];
          if (!hsp) return null;

          const stats = getAlignmentStats(hsp.qseq, hsp.hseq);

          return {
            title: hit.description?.[0]?.title || "No title",
            accession: hit.description?.[0]?.accession || "",
            percent_identity: stats.length ? (stats.matches / stats.length) * 100 : 0,
            alignment_length: stats.length,
            matches: stats.matches,
            mismatches: stats.mismatches,
            gaps: stats.gaps,
            query_coverage: hsp.query_len ? (stats.length / hsp.query_len) * 100 : 0,
            evalue: hsp.evalue,
            bit_score: hsp.bit_score,
            hsp_start: hsp.query_from,
            hsp_end: hsp.query_to,
            query_length: hsp.query_len,
            alignment: { query: hsp.qseq, subject: hsp.hseq },
            aligned_lines: generateAlignedLines(hsp.qseq, hsp.hseq, hsp.query_from, hsp.hit_from)
          };
        }).filter(Boolean);

        setResults(mappedResults);
        setMetadata({ source: "NCBI", program: blastType, query_length: sequenceInput.length });
        setSelectedTabIndex(0);
        setProgressMsg("Results loaded");
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Fallback: local BLAST
    handleSubmit();
  };

  const handleSubmit = async () => {
    if (!sequenceInput.trim()) {
      setError("You must enter a sequence or upload a file.");
      return;
    }
    setLoading(true);
    setProgressMsg("Initializing BLAST...");
    setResults([]);
    setMetadata(null);
    setError(null);
    setNoResultsInfo(null);
    setSelectedAlignmentIndex(null);
    setShowAllAlignments(false);
    setDisplayAlignments([]);

    try {
      const url = "/api/blast/";
      const body = {
        input: sequenceInput.trim(),
        blast_type: blastType,
        genome: blastType === "blastp" || blastType === "blastx" ? proteinDB : genome,
        evalue: expectValue,
        max_alignments: Math.max(100, parseInt(maxAlignments) || 50),
        word_size: parseInt(wordSize) || 11,
        alignment_mode: alignmentMode,
        blast_source: blastSource === "ncbi" ? "remote" : "local",
      };
      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const errData = await resp.json();
        throw new Error(errData.error || "BLAST request failed");
      }
      const data = await resp.json();
      
      // Check if there are results or if it's a no-results response
      if (data.results && data.results.length > 0) {
        setResults(
          data.results.map((r) => ({
            ...r,
            accession: r.accession || extractAccession(r.title),
          }))
        );
        setMetadata(data.metadata || null);
        setSelectedTabIndex(0);
        setNoResultsInfo(null);
      } else if (data.suggestions || data.message) {
        // No results with helpful information
        setNoResultsInfo({
          message: data.message || "No matches were found in the database.",
          suggestions: data.suggestions || [],
          sequence_analysis: data.sequence_analysis || {},
          recommended_blast: data.recommended_blast || null,
        });
        setResults([]);
        setMetadata(null);
      } else {
        // Empty results
        setResults([]);
        setMetadata(null);
        setNoResultsInfo({
          message: "No matches were found in the database.",
          suggestions: [
            "Try a different database",
            "Try adjusting the E-value threshold",
            "Check that the sequence type matches the BLAST program",
          ],
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredResults = results.filter(
    (r) =>
      r.percent_identity >= 0 &&
      r.query_coverage >= 0 &&
      r.evalue <= 1
  );

  const handleShowAlignments = () => {
    if (showAllAlignments) {
      setShowAllAlignments(false);
      setDisplayAlignments([]);
    } else {
      setShowAllAlignments(true);
      setDisplayAlignments(results.map((r, i) => ({ ...r, index: i })));
      setSelectedTabIndex(1);
    }
  };

  // --- Effect: Render phylogenetic tree ---
  useEffect(() => {
    if (results.length && results.tree) {
      if (treeRef.current) {
        treeRef.current.innerHTML = "";
        const tree = new phylotree(results.tree);
        tree.render({ container: treeRef.current });
      }
    }
  }, [results]);

  // --- Tooltip ---
  useEffect(() => {
    if (filteredResults.length) {
      tippy("[data-tippy-content]", { allowHTML: true, theme: "light" });
    }
  }, [filteredResults]);

  // --- Download functions ---
  const downloadResultsAsCSV = () => {
    const headers = ["Title", "%Identity", "Length", "Coverage", "E-value", "Score"];
    const csvRows = [
      headers.join(","),
      ...filteredResults.map((r) =>
        [
          `"${r.title}"`,
          r.percent_identity.toFixed(2),
          r.alignment_length,
          r.query_coverage,
          r.evalue,
          r.bit_score.toFixed(1),
        ].join(",")
      ),
    ];
    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
    const link = document.createElement("a");
    link.href = encodeURI(csvContent);
    link.download = "blast_hits.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAlignmentFile = () => {
    if (results.length === 0 || (selectedAlignmentIndex === null && !showAllHitsGraphically))
      return;
    if (showAllHitsGraphically) {
      const fasta = results
        .map((r, i) => `>Result_${i + 1}\n${r.alignment.query}\n${r.alignment.subject}`)
        .join("\n");
      const blob = new Blob([fasta], { type: "text/fasta" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "all_alignments.fasta";
      a.click();
    } else {
      const r = results[selectedAlignmentIndex];
      const fasta = `>Alignment\n${r.alignment.query}\n${r.alignment.subject}`;
      const blob = new Blob([fasta], { type: "text/fasta" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "alignment.fasta";
      a.click();
    }
  };

  const getAlignmentIndexFromMouse = (e, hit, barRef) => {
    const rect = barRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = Math.max(0, Math.min(1, x / rect.width));
    const qLen = hit.query_length;
    const queryPos = Math.floor(pct * qLen);
    return Math.min(queryPos, hit.alignment.query.length - 1);
  };

  const renderAlignmentLens = () => {
    if (!hoveredAlignment) return null;

    const { hit, index } = hoveredAlignment;
    const win = 15;

    const q = hit.alignment.query;
    const s = hit.alignment.subject;

    const start = Math.max(0, index - win);
    const end = Math.min(q.length, index + win);

    const qSlice = q.slice(start, end);
    const sSlice = s.slice(start, end);
    const symbols = getAlignmentSymbols(qSlice, sSlice);

    return (
      <div className="alignment-lens">
        <strong>{hit.title}</strong>
        <pre>
          Q: {renderColoredBlock(qSlice, symbols)}
          {" ".repeat(index - start)}^
          S: {renderColoredBlock(sSlice, symbols)}
        </pre>
        <div className="lens-meta">
          Pos: {index + 1} | %ID:{" "}
          {hit.percent_identity.toFixed(1)} | E: {hit.evalue}
        </div>
      </div>
    );
  };

  // --- Render graphical hit with sequence alignment ---
  const renderGraphicalHit = (hit, index) => {
    if (!hit || !hit.alignment) return null;
    
    const { query, subject } = hit.alignment;
    const alignedLines = generateAlignedLines(query, subject, 1, 1);

    return (
      <div style={{ position: 'relative', width: '100%', overflowX: 'auto' }}>
        {hoveredResult && (
          <div
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              background: '#fff',
              border: '1px solid #ccc',
              padding: '10px',
              zIndex: 999,
              boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
            }}
          >
            <h4>{hoveredResult.title}</h4>
            <p>%Identity: {hoveredResult.percent_identity.toFixed(2)}%</p>
            <p><strong>Score:</strong> {hoveredResult.bit_score}</p>
            <p><strong>Match:</strong> {hoveredResult.match}</p>
            <p><strong>Mismatch:</strong> {hoveredResult.mismatch}</p>
            <p><strong>Gaps:</strong> {hoveredResult.gaps}</p>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          {alignedLines.map((line, idx) => (
            <div key={idx} style={{ display: 'flex', width: '100%', marginBottom: '8px' }}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                <strong style={{ minWidth: '80px' }}>Query:</strong>
                <div
                  style={{ flex: 1, display: 'flex', cursor: 'pointer' }}
                  onMouseEnter={() => setHoveredResult(hit)}
                  onMouseLeave={() => setHoveredResult(null)}
                >
                  {renderColoredBlock(line.qSeq, line.symbols)}
                </div>
              </div>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                <strong style={{ minWidth: '80px' }}>Subject:</strong>
                <div
                  style={{ flex: 1, display: 'flex', cursor: 'pointer' }}
                  onMouseEnter={() => setHoveredResult(hit)}
                  onMouseLeave={() => setHoveredResult(null)}
                >
                  {renderColoredBlock(line.sSeq, line.symbols)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const [expandedIndex, setExpandedIndex] = useState(null);

  const toggleExpand = (index) => {
    setExpandedIndex(prev => (prev === index ? null : index));
  };

  // =================== BlastBar Component ===================
  function BlastBar({
    hit,
    index,
    onHover,
    onToggleExpand,
    getAlignmentIndexFromMouse,
    isExpanded,
    selectedHit,
    maxAlignmentLength,
  }) {
    const barRef = useRef(null);

    const barWidthPercent = (hit.alignment_length / maxAlignmentLength) * 100;

    const handleMouseMove = (e) => {
      if (onHover && barRef.current) {
        const hitIndex = getAlignmentIndexFromMouse(e, hit, barRef);
        onHover({ hit, index, hitIndex });
      }
    };

    const stats = hit.alignment
      ? getAlignmentStats(hit.alignment.query, hit.alignment.subject)
      : null;

    const alignedLines = hit.alignment
      ? generateAlignedLines(hit.alignment.query, hit.alignment.subject, 1, 1)
      : [];

    return (
      <div style={{ marginBottom: 14 }}>
        <div
          ref={barRef}
          className="blast-bar"
          onMouseMove={handleMouseMove}
          onMouseLeave={() => onHover(null)}
          onClick={() => onToggleExpand(index)}
          style={{ cursor: "pointer", position: "relative", height: 16 }}
        >
          <div
            style={{
              position: "absolute",
              left: `${((hit.hsp_start - 1) / hit.query_length) * 100}%`,
              width: `${barWidthPercent}%`,
              top: 0,
              bottom: 0,
              backgroundColor: getBlastColorByScore(hit.bit_score),
              borderRadius: "6px",
              opacity: hit === selectedHit ? 1 : 0.85,
            }}
          />
        </div>

        {isExpanded && (
          <div
            style={{
              padding: "8px",
              background: "#f9f9f9",
              border: "1px solid #ccc",
              marginTop: 4,
            }}
          >
            <h4>{hit.title}</h4>
            <p><strong>%Identity:</strong> {hit.percent_identity.toFixed(2)}%</p>
            <p><strong>Bit Score:</strong> {hit.bit_score}</p>
            <p><strong>Alignment Length:</strong> {hit.alignment_length}</p>
            <p><strong>Query Coverage:</strong> {hit.query_coverage}%</p>
            <p><strong>E-value:</strong> {hit.evalue}</p>

            {hit.alignment && (
              <div style={{ marginTop: 12 }}>
                {alignedLines.map((line, idx) => (
                  <div key={idx} style={{ marginBottom: 8 }}>
                    <pre style={{ margin: 0 }}>
                      Query {line.qStart.toString().padStart(5)}{" "}
                      {renderColoredBlock(line.qSeq, line.symbols)}{" "}
                      {line.qEnd}
                    </pre>
                    <pre style={{ margin: 0 }}>
                      Sbjct {line.sStart.toString().padStart(5)}{" "}
                      {renderColoredBlock(line.sSeq, line.symbols)}{" "}
                      {line.sEnd}
                    </pre>
                  </div>
                ))}
                {stats && (
                  <div style={{ marginTop: 8, fontSize: 12 }}>
                    <span><strong>Matches:</strong> {stats.matches} </span>
                    <span><strong>Mismatches:</strong> {stats.mismatches} </span>
                    <span><strong>Gaps:</strong> {stats.gaps}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ===================== Drag & hover logic =====================
  const [draggingHit, setDraggingHit] = useState(null);
  const [dragStartX, setDragStartX] = useState(0);

  const handleGraphDragStart = (e, hit) => {
    e.preventDefault();
    setDraggingHit(hit);
    setDragStartX(e.clientX);
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!draggingHit) return;
      const deltaX = e.clientX - dragStartX;
      if (Math.abs(deltaX) > 50) {
        setHoveredResult(draggingHit);
      }
    },
    [draggingHit, dragStartX]
  );

  const handleMouseUp = () => setDraggingHit(null);

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove]);

  // ===================== Loading =====================
  if (loading) {
    return (
      <div className="blast-loading-screen">
        <div className="blast-loading-box">
          <div className="spinner" />
          <h2>Running BLAST…</h2>
          <p>{progressMsg}</p>
        </div>
      </div>
    );
  }

  // ===================== Error =====================
  if (error) {
    return (
      <div className="blast-error">
        <h3>Error: {error}</h3>
        <button className="blast-back-btn" onClick={handleBack}>
          Back to Input
        </button>
      </div>
    );
  }

  // ===================== Results =====================
  // Show results if we have any results (even if filteredResults is empty due to filter)
  if (results.length > 0) {
    const displayResults = filteredResults.length > 0 ? filteredResults : results;
    
    return (
      <div className="blast-container">
        {/* ================= TOP BUTTONS ================= */}
        <div className="blast-top-buttons">
          <button className="blast-back-btn" onClick={handleBack}>
            ← Back to Input
          </button>

          <button className="blast-run-btn" onClick={downloadResultsAsCSV}>
            Download Hits CSV
          </button>

          {selectedAlignmentIndex !== null && (
            <button className="blast-run-btn" onClick={downloadAlignmentFile}>
              Download Alignment
            </button>
          )}

          <button className="blast-run-btn" onClick={handleShowAlignments}>
            {showAllAlignments ? "Hide All Alignments" : "Show All Alignments"}
          </button>
        </div>

        {/* ================= METADATA ================= */}
        {metadata && (
          <div className="blast-metadata">
            {Object.entries(metadata).map(([k, v]) => (
              <p key={k}>
                <strong>{k.replace(/_/g, " ")}:</strong> {v}
              </p>
            ))}
          </div>
        )}

        {/* ================= TABS ================= */}
        <Tabs selectedIndex={selectedTabIndex} onSelect={setSelectedTabIndex}>
          <TabList>
            <Tab>Hits</Tab>
            <Tab>Alignments</Tab>
            <Tab>Graphics</Tab>
          </TabList>

          {/* ---------- Hits ---------- */}
          <TabPanel>
            <table className="blast-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>%Identity</th>
                  <th>Length</th>
                  <th>Coverage</th>
                  <th>E-value</th>
                  <th>Score</th>
                </tr>
              </thead>
              <tbody>
                {displayResults.map((r, i) => (
                  <tr key={i}>
                    <td
                      style={{ cursor: "pointer", textDecoration: "underline" }}
                      onClick={() => {
                        setSelectedAlignmentIndex(i);
                        setSelectedTabIndex(1);
                      }}
                    >
                      {r.title}
                    </td>
                    <td>{r.percent_identity.toFixed(2)}</td>
                    <td>{r.alignment_length}</td>
                    <td>{r.query_coverage}%</td>
                    <td>{r.evalue}</td>
                    <td>{r.bit_score.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TabPanel>

          {/* ---------- Alignments ---------- */}
          <TabPanel>
            {(showAllAlignments
              ? displayResults
              : selectedAlignmentIndex !== null
              ? [displayResults[selectedAlignmentIndex]]
              : []
            ).map((r, i) => {
              if (!r?.alignment) return null;

              const { query, subject } = r.alignment;
              const stats = getAlignmentStats(query, subject);
              const lines = generateAlignedLines(query, subject, 1, 1);

              return (
                <div key={i} className="alignment-hit-block">
                  <div className="alignment-hit-header">
                    <h4>{r.title}</h4>
                    <div className="alignment-stats">
                      <span><strong>%Identity:</strong> {r.percent_identity.toFixed(2)}</span>
                      <span><strong>Length:</strong> {stats.length}</span>
                      <span><strong>Match:</strong> {stats.matches}</span>
                      <span><strong>Mismatch:</strong> {stats.mismatches}</span>
                      <span><strong>Gaps:</strong> {stats.gaps}</span>
                      <span><strong>E-value:</strong> {r.evalue}</span>
                      <span><strong>Score:</strong> {r.bit_score}</span>
                    </div>
                  </div>

                  <div className="alignment-seq-block">
                    {lines.map((line, idx) => (
                      <div key={idx} className="alignment-line-block">
                        <pre>
                          Query {line.qStart.toString().padStart(5)}{" "}
                          {renderColoredBlock(line.qSeq, line.symbols)}{" "}
                          {line.qEnd}
                        </pre>
                        <pre>
                          Sbjct {line.sStart.toString().padStart(5)}{" "}
                          {renderColoredBlock(line.sSeq, line.symbols)}{" "}
                          {line.sEnd}
                        </pre>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </TabPanel>

          {/* ---------- Graphics ---------- */}
          <TabPanel>
            <BlastLegend />

            <div className="graphics-container">
              {displayResults.map((hit, i) => (
                <div key={i} className="graphics-hit-block">
                  <div className="graphics-hit-header">
                    <h4>{hit.title}</h4>
                    <div className="graphics-hit-meta">
                      <span>%Identity: {hit.percent_identity.toFixed(1)}</span>
                      <span>Length: {hit.alignment_length}</span>
                      <span>E-value: {hit.evalue}</span>
                      <span>Bit-Score: {hit.bit_score}</span>
                    </div>
                  </div>

                  <BlastBar
                    hit={hit}
                    index={i}
                    onHover={setHoveredHit}
                    getAlignmentIndexFromMouse={getAlignmentIndexFromMouse}
                    onToggleExpand={(idx) =>
                      setExpandedHitIndex(expandedHitIndex === idx ? null : idx)
                    }
                    isExpanded={expandedHitIndex === i}
                    selectedHit={selectedHit}
                    maxAlignmentLength={Math.max(
                      ...displayResults.map(h => h.alignment_length)
                    )}
                  />

                  <div className="graphics-buttons">
                    <button
                      className="blast-run-btn"
                      onClick={downloadResultsAsCSV}
                    >
                      Download Hits CSV
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </TabPanel>
        </Tabs>
      </div>
    );
  }

  // ===================== No Results =====================
  if (noResultsInfo && !loading && !error) {
    const info = noResultsInfo;
    const analysis = analyzeSequence(sequenceInput, blastType);

    return (
      <div className="blast-container">
        <div className="blast-no-results">
          <div className="no-results-icon">🔍</div>
          <h3>No BLAST hits found</h3>
          <p className="no-results-message">{info.message}</p>

          {info.sequence_analysis && info.sequence_analysis.warnings && info.sequence_analysis.warnings.length > 0 && (
            <div className="sequence-analysis">
              <strong>Sequence Analysis:</strong>
              <ul>
                {info.sequence_analysis.warnings.map((warning, idx) => (
                  <li key={idx}>{warning}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="suggestions-box">
            <h4>💡 Suggestions:</h4>
            <ul>
              {(info.suggestions || []).map((suggestion, idx) => (
                <li key={idx}>{suggestion}</li>
              ))}
            </ul>
          </div>

          {analysis.length > 0 && (
            <div className="why-no-results">
              <button 
                className="why-toggle-btn"
                onClick={() => setShowWhyNoResults(!showWhyNoResults)}
              >
                {showWhyNoResults ? 'Hide Analysis' : '🔍 Why no results?'}
              </button>
              
              {showWhyNoResults && (
                <div className="analysis-details">
                  <h4>Sequence Analysis:</h4>
                  <ul>
                    {analysis.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                  <div className="analysis-tip">
                    <strong>💡 Tip:</strong> For {blastType === 'blastx' ? 'blastx' : 'this BLAST type'}, 
                    use a {blastType === 'blastx' ? 'protein-coding sequence (CDS)' : 'sequence that matches the database type'}.
                  </div>
                </div>
              )}
            </div>
          )}

          {info.recommended_blast && (
            <div className="recommendation-box">
              <h4>🔄 Recommended Action:</h4>
              <p>
                Your sequence appears to be <strong>non-coding</strong>.
                Try running with <strong>{info.recommended_blast}</strong> instead:
              </p>
              <button
                className="blast-run-btn"
                onClick={() => {
                  setBlastType(info.recommended_blast);
                  if (info.recommended_blast === "blastn") {
                    setGenome("default_genome");
                  }
                  handleRun();
                }}
                style={{ background: '#2196F3' }}
              >
                Run with {info.recommended_blast} →
              </button>
            </div>
          )}

          <button className="blast-back-btn" onClick={handleBack} style={{ marginTop: '20px' }}>
            ← Back to Input
          </button>
        </div>
      </div>
    );
  }

  // ===================== Input Form (DEFAULT VIEW) =====================
  return (
    <div className="blast-container">
      {/* ---------- Header with Help and NCBI BLAST ---------- */}
      <div className="blast-header">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <button
            onClick={() => navigate("/home")}
            className="blast-back-btn"
            style={{ marginRight: "10px" }}
          >
            ← Home
          </button>

          <h2 style={{ margin: 0 }}>Teff BLAST Portal</h2>

          <div />
        </div>

        <button
          className="help-btn"
          onClick={() => setShowHelp(true)}
          title="How to use BLAST"
        >
          Help ❔
        </button>

        <button
          className="ncbi-btn"
          onClick={() => {
            const seq = encodeURIComponent(sequenceInput);
            window.open(
              `https://blast.ncbi.nlm.nih.gov/Blast.cgi?PROGRAM=${blastType}&PAGE_TYPE=BlastSearch&QUERY=${seq}`,
              "_blank"
            );
          }}
          disabled={!sequenceInput}
        >
          NCBI BLAST →
        </button>
      </div>

      {/* ---------- Help Modal ---------- */}
      {showHelp && (
        <div
          className="help-modal-overlay"
          onClick={() => setShowHelp(false)}
        >
          <div
            className="help-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>BLAST Help</h3>
            <ul>
              <li><strong>Input Sequence:</strong> Paste a FASTA sequence.</li>
              <li><strong>File Upload:</strong> Upload a <code>.fasta</code>, <code>.fa</code>, or <code>.txt</code> file.</li>
              <li><strong>BLAST Source:</strong> Currently local BLAST searches are supported.</li>
              <li><strong>BLAST Type:</strong> Choose <em>blastn</em> for DNA or <em>blastp</em> for protein sequences.</li>
              <li><strong>Database:</strong> Select the genome/protein database for your search.</li>
              <li><strong>Advanced Options:</strong> Optional parameters like E-value, max alignments, word size, and alignment mode.</li>
              <li><strong>Run:</strong> Click "Run BLAST" to perform the search. Results will display below as interactive graphs.</li>
              <li><strong>NCBI BLAST:</strong> Use the NCBI BLAST button to run your query on the official NCBI BLAST platform.</li>
            </ul>
            <button onClick={() => setShowHelp(false)}>Close</button>
          </div>
        </div>
      )}

      {/* ---------- Input Sequence ---------- */}
      <div className="blast-step">
        <h3>Input Sequence</h3>

        <textarea
          rows={6}
          placeholder="Paste FASTA sequence"
          value={sequenceInput}
          onChange={(e) => setSequenceInput(e.target.value)}
        />

        <div className="file-upload-row">
          <input
            type="file"
            accept=".fasta,.fa,.txt"
            onChange={handleFileUpload}
          />

          {sequenceInput && (
            <button
              className="clear-seq-btn"
              onClick={clearSequence}
              title="Clear sequence"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* ---------- Parameters ---------- */}
      <div className="blast-step">
        <h3>Parameters</h3>

        <label>BLAST Source</label>
        <select
          value={blastSource}
          onChange={(e) => setBlastSource(e.target.value)}
        >
          <option value="local">Local BLAST</option>
        </select>

        <label>BLAST Type</label>
        <select
          value={blastType}
          onChange={(e) => setBlastType(e.target.value)}
        >
          <option value="blastn">blastn (DNA → DNA)</option>
          <option value="blastp">blastp (Protein → Protein)</option>
          <option value="blastx">blastx (DNA → Protein)</option>
        </select>

        <label>Database</label>
        {blastType === "blastn" ? (
          <select value={genome} onChange={(e) => setGenome(e.target.value)}>
            <option value="default_genome">DNA: default_genome</option>
            <option value="repeat_masked">DNA: Repeat Masked</option>
            <option value="ncbi_legacy">NCBI Legacy Assembly</option>
          </select>
        ) : (
          <select value={proteinDB} onChange={(e) => setProteinDB(e.target.value)}>
            <option value="default_protein">Protein: Default</option>
            <option value="ncbi_refseq">Protein: NCBI RefSeq</option>
            <option value="uniprot">Protein: UniProt</option>
          </select>
        )}

        {/* ---------- Advanced Options ---------- */}
        <button
          onClick={() => setShowAdvanced((p) => !p)}
          style={{ marginTop: "10px" }}
        >
          {showAdvanced ? "Hide Advanced" : "Show Advanced"}
        </button>

        {showAdvanced && (
          <div className="blast-advanced-options">
            <label>E-value</label>
            <input
              value={expectValue}
              onChange={(e) => setExpectValue(e.target.value)}
            />

            <label>Max Alignments</label>
            <input
              type="number"
              value={maxAlignments}
              onChange={(e) => setMaxAlignments(e.target.value)}
            />

            <label>Word Size</label>
            <input
              type="number"
              value={wordSize}
              onChange={(e) => setWordSize(e.target.value)}
            />

            <label>Alignment Mode</label>
            <select
              value={alignmentMode}
              onChange={(e) => setAlignmentMode(e.target.value)}
            >
              <option value="auto">Auto</option>
              <option value="global">Global</option>
              <option value="local">Local</option>
            </select>
          </div>
        )}

        {/* ---------- Run ---------- */}
        <button
          className="blast-run-btn"
          onClick={handleRun}
          style={{ marginTop: "12px" }}
        >
          Run BLAST
        </button>

        {error && <p className="blast-error">{error}</p>}
        {loading && <p className="blast-loading">{progressMsg}</p>}
      </div>
    </div>
  );
};

export default BlastComponent;
